import { type AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';

import * as carrierFormFieldsActions from '~/actions/carrier-form-fields.actions';
import { ActionKeys } from '~/actions/constants';
import * as carrierFormFieldsService from '~/services/carrier-form-fields.service';
import * as CarrierFormField from '~/types/CarrierFormField';

export function* fetchCarrierFormFields(
  action: ReturnType<typeof carrierFormFieldsActions.fetchCarrierFormFields>,
) {
  const setValidations = (carrierFormFields: CarrierFormField.CarrierFormField[]) => {
    const validations: { [key: string]: ((data: string) => void)[] } = {};

    // Validation functions
    const validateIsRequired = (value: string) => (!value ? 'Required' : undefined);

    const validateMaxLength = (max: number) => (value: string) =>
      value && value.length > max
        ? `Maximum length is ${max} characters (currently ${value.length})`
        : undefined;

    const validateMinLength = (min: number) => (value: string) =>
      value && value.length < min
        ? `Minimum length is ${min} characters (currently ${value.length})`
        : undefined;

    const validateIntegerType = (value: unknown) =>
      value && isNaN(Number(value)) ? 'Must be a number' : undefined;

    for (const parameter of carrierFormFields) {
      const parameterValidations: ((data: string) => void)[] = [];

      if (parameter.fieldType === 'integer') parameterValidations.push(validateIntegerType);

      if (parameter.isRequired) parameterValidations.push(validateIsRequired);

      if (parameter.maxLength) parameterValidations.push(validateMaxLength(parameter.maxLength));

      if (parameter.minLength) parameterValidations.push(validateMinLength(parameter.minLength));

      validations[parameter.key] = parameterValidations;
    }

    return validations;
  };

  try {
    const response: AxiosResponse<{ params: Maybe<CarrierFormField.JSONCarrierFormField[]> }> =
      yield call(
        carrierFormFieldsService.fetchCarrierFormFields,
        action.payload.carrier,
        action.payload.formType,
      );

    // The backend returns { params: null } for carriers with no DB-backed form fields
    // (e.g. royal_mail_sf). Fall through to the "not supported" path below instead of
    // crashing on null.reduce().
    const carrierFormFields = (response.data.params ?? []).reduce<
      CarrierFormField.CarrierFormField[]
    >((results, param) => {
      const serialized = CarrierFormField.fromJSON(param);
      if (serialized) results.push(serialized);

      return results;
    }, []);

    // If there aren't any form fields then the carrier form is not migrated to React yet
    if (carrierFormFields.length === 0)
      throw { response: { data: 'This carrier form is currently not supported.' } };

    // Calculate the validations for each field
    const carrierFormFieldValidations = setValidations(carrierFormFields);

    // Serialized the response and filter out invalid form fields
    const successPayload: carrierFormFieldsActions.FetchCarrierFormFieldsSuccessPayload = {
      carrierFormFieldValidations,
      carrierFormFields,
    };

    yield put(carrierFormFieldsActions.fetchCarrierFormFieldsSuccess(successPayload));
  } catch (err) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const errorMessage = (err as any).response
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any).response.data
      : 'An error occurred while retrieving the form. Please contact support.';

    yield put(carrierFormFieldsActions.fetchCarrierFormFieldsError({ error: errorMessage }));
  }
}

export default [takeLatest(ActionKeys.FETCH_CARRIER_FORM_FIELDS, fetchCarrierFormFields)];
